import React from 'react';
import { Popover, Stack, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { AutocompleteHighlighter, AutocompleteRaw, BaseTextField, SelectRaw } from '@uplift/ui-v2';
import { SourceChipFilterValue } from './ChipFilters';
import { AssessmentOption } from './hooks';


const useStyles = makeStyles()(theme => ({
  listbox: {
    '&& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: theme.palette.secondary.background,

      '&.Mui-focused': {
        backgroundColor: theme.palette.secondary.background,
      },
    },
  },
}));


const ALL_SOURCES = 'all';

const SOURCE_ITEMS = [
  { value: ALL_SOURCES, label: 'All sources' },
  { value: 'patient', label: 'Patient-completed' },
  { value: 'administer', label: 'Clinician-administered' },
];


export const AssessmentSelect: React.VFC<{
  options: AssessmentOption[];
  value: AssessmentOption;
  setValue: (option: AssessmentOption) => void;
}> = (props) => {
  const { classes } = useStyles();
  const timezone = useTimezone();
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [ anchorEl, setAnchorEl ] = React.useState<HTMLDivElement | null>(null);
  const [ inputValue, setInputValue ] = React.useState('');

  const lastCompletedLabel = React.useCallback((option: AssessmentOption) => {
    if (!option.lastCompletedAt) {
      return 'No submissions';
    }

    return `Last ${CalendarDateService.date(option.lastCompletedAt).tz(timezone).format('MMM D')}`;
  }, [ timezone ]);

  const handleOpen = () => setAnchorEl(triggerRef.current);

  const handleClose = () => {
    setAnchorEl(null);
    setInputValue('');
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <>
      <BaseTextField
        ref={triggerRef}
        label="Assessment"
        color="secondary"
        fullWidth
        value={props.value.name}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        slotProps={{
          input: {
            readOnly: true,
            role: 'combobox',
            'aria-expanded': Boolean(anchorEl),
            sx: { height: '48px', cursor: 'pointer' },
            endAdornment: (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="textSecondary" noWrap>
                  {lastCompletedLabel(props.value)}
                </Typography>
                {anchorEl ? <ArrowDropUp color="action" /> : <ArrowDropDown color="action" />}
              </Stack>
            ),
          },
        }}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: anchorEl?.offsetWidth, p: 1, overflow: 'visible' } } }}
      >
        <AutocompleteRaw
          autoFocus
          openOnFocus
          disablePortal
          name="assessment"
          color="secondary"
          size="small"
          fullWidth
          disableClearable
          classes={{ listbox: classes.listbox }}
          options={props.options}
          value={props.value}
          setValue={(option?: AssessmentOption) => {
            if (!option) {
              return;
            }

            props.setValue(option);
            handleClose();
          }}
          inputValue={inputValue}
          setInputValue={setInputValue}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          placeholder="Search assessments"
          noOptionsText="No assessments match"
          renderOption={option => (
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} width="100%">
              <Typography variant="body2">
                <AutocompleteHighlighter text={option.name} highlight={inputValue} />
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {lastCompletedLabel(option)}
              </Typography>
            </Stack>
          )}
        />
      </Popover>
    </>
  );
};


export const SourceSelect: React.VFC<{
  value: SourceChipFilterValue;
  setValue: (value: SourceChipFilterValue) => void;
}> = (props) => {
  return (
    <SelectRaw
      name="source"
      label="Source"
      size="small"
      fullWidth
      value={props.value ?? ALL_SOURCES}
      onChange={event => props.setValue(event.target.value === ALL_SOURCES ? undefined : event.target.value as SourceChipFilterValue)}
      items={SOURCE_ITEMS}
    />
  );
};
